import React from 'react';
import { niceYAxis } from '../../utils/chartAxisUtils';

export default function FitnessProfileWeightLineChart({ data = [], trendLine = [], weightGoal = null }) {
	const chartValues = [
		...data.map((e) => e.value),
		...trendLine.map((e) => e.value)
	].filter((v) => Number.isFinite(v));

	if (!chartValues.length) {
		return (
			<p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No weight data</p>
		);
	}

	const dataMin = Math.min(...chartValues);
	const dataMax = Math.max(...chartValues);
	const span = dataMax - dataMin;
	const pad = span === 0 ? 6 : Math.max(3, span * 0.06);
	let paddedMin = dataMin - pad;
	let paddedMax = dataMax + pad;
	if (paddedMax - paddedMin < 12) {
		const mid = (dataMin + dataMax) / 2;
		paddedMin = mid - 6;
		paddedMax = mid + 6;
	}
	const { axisMin: min, axisMax: max, range, ticks: yTicks } = niceYAxis(paddedMin, paddedMax);
	const pointCount = Math.max(data.length, 1);

	const toY = (value) => 100 - ((value - min) / range) * 100;
	const toX = (index) => (index / Math.max(data.length - 1, 1)) * 100;

	const loggedPoints = data
		.map((entry, index) => (Number.isFinite(entry.value) ? `${toX(index)},${toY(entry.value)}` : null))
		.filter(Boolean)
		.join(' ');

	const trendPoints = trendLine
		.map((entry) => {
			const dataIndex = data.findIndex((point) => point.date === entry.date);
			if (dataIndex < 0) return null;
			return `${toX(dataIndex)},${toY(entry.value)}`;
		})
		.filter(Boolean)
		.join(' ');

	return (
		<div className="mt-4">
			<div className="flex gap-3">
				<div className="w-11 shrink-0 h-48 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 text-right">
					{yTicks.map((tick, index) => (
						<span key={`${tick}-${index}`}>{tick}</span>
					))}
				</div>
				<svg viewBox="0 0 100 100" className="flex-1 h-48 overflow-visible">
					{trendLine.length > 1 && (
						<polyline
							fill="none"
							stroke="#94a3b8"
							strokeWidth="2"
							strokeDasharray="6 4"
							points={trendPoints}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{loggedPoints && (
						<polyline
							fill="none"
							stroke="#2563eb"
							strokeWidth="2"
							points={loggedPoints}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{data.map((entry, index) => {
						if (!Number.isFinite(entry.value)) return null;
						return (
							<circle
								key={`${entry.date}-${entry.value}`}
								cx={toX(index)}
								cy={toY(entry.value)}
								r="2.3"
								fill="#1d4ed8"
							/>
						);
					})}
				</svg>
			</div>
			<div
				className="mt-1 grid text-xs text-gray-500 dark:text-gray-400"
				style={{ gridTemplateColumns: `repeat(${pointCount}, minmax(0, 1fr))` }}
			>
				{data.map((entry, index) => (
					<span key={entry.date || `${entry.month}-${index}`} className="text-center">
						{entry.month}
					</span>
				))}
			</div>
			{weightGoal && trendLine.length > 0 && (
				<div className="mt-3 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-0.5 w-5 border-t-2 border-dashed border-slate-400" />
						Goal pace ({weightGoal.target_metric})
					</div>
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-2 w-2 rounded-full bg-blue-700" />
						Logged weight
					</div>
				</div>
			)}
		</div>
	);
}
